import moment from 'moment-timezone';
import { navigate } from 'gatsby';
import v from 'voca';
import {
  ApolloClient, createHttpLink, gql, InMemoryCache, from
} from '@apollo/client';
import { toast } from 'react-toastify';
import i18n from 'i18n-js';

import store from '../store';
import SecureStore from '../utils/secureStore';
// eslint-disable-next-line import/no-cycle
import { getProfile } from '../store/actions/userActions';
import { isBrowser } from '../utils/windowUtils';
import { getTokenQuery } from '../graphql/auth';
// eslint-disable-next-line import/no-cycle
import { setCurrentCompany } from '../store/actions/companyActions';
import {
  isCommunityProjectAdmin,
  isCompanyUser,
  isMasterAdmin,
  isSalesUser
} from '../utils/authUtils';
import { setLoginEmail } from '../store/actions/authActions';
import { uuidv4 } from '../utils/randomUtils';
import DataDogService from './DataDogService';
import PendoService from './PendoService';
import publicLink from '../apiClient/publicLink';
import { userHasSalesAppAccess } from '../utils/userUtils';
import { isSalesApp } from '../utils/appTypeUtils';
import { isCustomerDetailsPage } from '../utils/customerUtils';

let isSessionExpired = false;

function setTimezone(timezone) {
  console.log('Set user timezone: ', timezone);
  moment.tz.setDefault(timezone);
}

function processAuthData(authData) {
  SecureStore.setAuthData(authData);
  SecureStore.setAccessToken(authData.accessToken);
}

/**
 * Logout and clear current user data
 * @param navigateImmediately {boolean} - Default true
 * @returns {Promise<void>}
 */
async function logout(navigateImmediately = true) {
  DataDogService.clearUser();
  PendoService.clearUser();
  SecureStore.resetSession();
  await store.dispatch(setLoginEmail(null));

  if (navigateImmediately) navigateToRoot(null);
}

/**
 * Navigate to root screen
 * @param currentUser
 */
function navigateToRoot(currentUser) {
  if (currentUser) {
    if (isSalesApp() && userHasSalesAppAccess(currentUser)) {
      navigate('/sales/main');
      return;
    }

    if (isCommunityProjectAdmin() || isCompanyUser() || isSalesUser()) navigate('/admin/communities');
    else {
      const path = isMasterAdmin() ? '/admin/dashboard' : '/admin/overview';
      navigate(path);
    }
  } else navigate('/auth/login');
}

function navigateAfterLogin(currentUser) {
  const redirectPath = SecureStore.getLoginRedirectPath();

  if (v.isBlank(redirectPath)) {
    navigateToRoot(currentUser);
    return;
  }

  console.log('Redirecting after login to:', redirectPath);

  const isCustomerPage = isCustomerDetailsPage(redirectPath);
  if (isCustomerPage) navigate(redirectPath, { state: { backPath: '/sales/customers' } });
  else navigate(redirectPath);
}

async function login(navigateImmediately, authData = null) {
  if (authData) processAuthData(authData);

  return store.dispatch(getProfile())
    .then(async () => {
      const { currentUser } = store.getState().users;
      if (isSalesApp() && !userHasSalesAppAccess(currentUser)) {
        throw new Error('Unable to log in to the Sales App.');
      }
      await setCurrentUser(currentUser);
      if (navigateImmediately) navigateAfterLogin(currentUser);
    }).catch(async (error) => {
      await logout();
      throw error;
    });
}

async function setCurrentUser(currentUser) {
  console.log('User logged:', currentUser.email);
  const userCompanyId = SecureStore.getCurrentCompanyId() || currentUser.company.id;
  await store.dispatch(setCurrentCompany(userCompanyId));
  SecureStore.setCurrentCompanyId(userCompanyId);
  SecureStore.setRole(currentUser.role);
  DataDogService.setUser(currentUser);
  PendoService.setUser(currentUser);
  setTimezone(currentUser.timezone);
}

function isAccessTokenExpired() {
  const authData = SecureStore.getAuthData();
  if (authData) {
    const minutesBeforeExpiring = 5 * 60000; // Refresh access token 5 minutes before it expires
    const expiryDate = authData.createdAt + authData.expiresIn - minutesBeforeExpiring;

    const now = new Date().getTime();
    return expiryDate < now;
  }
  return false;
}

async function refreshAccessToken() {
  const { refreshToken } = SecureStore.getAuthData() || {};
  if (v.isBlank(refreshToken)) return null;

  console.log('Refreshing access token...');

  const variables = { refreshToken };

  const httpLink = createHttpLink({
    uri: process.env.GATSBY_API_URL
  });

  const links = from([
    publicLink,
    httpLink,
  ]);

  const client = new ApolloClient({
    link: links,
    cache: new InMemoryCache({
      addTypename: false
    })
  });

  let newAuthData = {};

  try {
    const response = await client.mutate({
      mutation: gql(getTokenQuery),
      variables,
      context: {
        isPublic: true
      }
    });

    console.log('Access token refreshed!');
    newAuthData = response.data.authData;
    processAuthData(newAuthData);
    return newAuthData.accessToken;
  } catch (error) {
    console.log('Error refreshing access token...', error.message);
    if (isSessionExpired) return null;

    isSessionExpired = true;
    toast.error(i18n.t('errors.sessionExpired'));
    setTimeout(async () => {
      await logout();
      isSessionExpired = false;
    }, 2000);

    throw error;
  }
}

function isAuthenticated() {
  if (!isBrowser()) return false;

  return !v.isBlank(SecureStore.getAccessToken());
}

/**
 * Create and assign a device token to browser
 */
function createDeviceToken() {
  let deviceToken = SecureStore.getDeviceToken();
  if (!v.isBlank(deviceToken)) return;

  deviceToken = uuidv4();
  console.log('Device token created...', deviceToken);

  SecureStore.setDeviceToken(deviceToken);
}

export default {
  login,
  logout,
  refreshAccessToken,
  isAccessTokenExpired,
  isAuthenticated,
  createDeviceToken
};
